import { ArrowRightIcon, MinusIcon, PlusIcon, SplitIcon } from 'lucide-react';
import { Fragment, memo } from 'react';
import {
  TransformComponent,
  TransformWrapper,
  useControls,
  useTransformComponent,
} from 'react-zoom-pan-pinch';

import { Button } from '@/components/core/button';
import { workflowTriggerTypeTitle } from '@/shared/title-maps';

import {
  AddNodeButton,
  AddPill,
  BranchRow,
  ConnectorDown,
  NodeCard,
  TriggerPill,
} from './canvas-nodes';
import { EditorActionBranch, EditorRule } from './editor-model';
import { getActionSummary } from './get-action-summary';
import { WorkflowEditorState, WorkflowEditorSelection } from './use-workflow-editor-state';
import type { ActionErrorAddress, RuleErrors } from './validate-workflow';

/**
 * The pannable, zoomable automation canvas: trigger pill → condition card per
 * rule → "If true"/"If false" action branches. Read-only apart from selection;
 * fills its container.
 */
export function WorkflowCanvas({ editor }: { editor: WorkflowEditorState }) {
  return (
    <div className="relative h-full w-full overflow-hidden bg-background bg-[radial-gradient(var(--color-border)_1px,transparent_1px)] [background-size:16px_16px]">
      <TransformWrapper
        minScale={0.25}
        maxScale={2}
        limitToBounds={false}
        centerOnInit
        doubleClick={{ disabled: true }}
        panning={{ velocityDisabled: true }}
        // the default wheel step (0.015 × |deltaY|) zooms a full mouse-wheel
        // tick (deltaY ≈ 120) straight to min/max scale
        wheel={{ step: 0.0015 }}
      >
        <ZoomControls />
        {/* w-max: fit-content would clamp wide forests and collapse the branch columns */}
        <TransformComponent
          wrapperClass="!h-full !w-full"
          wrapperProps={{ role: 'region', 'aria-label': 'Workflow canvas' }}
          contentClass="!p-12 !w-max"
        >
          <CanvasTree editor={editor} />
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
}

function ZoomControls() {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  const scaleLabel = useTransformComponent(({ state }) => (
    <span className="tabular-nums">{Math.round(state.scale * 100)}%</span>
  ));
  return (
    <div className="absolute top-4 right-4 z-10 flex items-center gap-0.5 rounded-lg border bg-card p-0.5 shadow-xs">
      <Button variant="ghost" size="icon-sm" aria-label="Zoom out" onClick={() => zoomOut()}>
        <MinusIcon />
      </Button>
      <Button variant="ghost" size="sm" aria-label="Reset zoom" onClick={() => resetTransform()}>
        {scaleLabel}
      </Button>
      <Button variant="ghost" size="icon-sm" aria-label="Zoom in" onClick={() => zoomIn()}>
        <PlusIcon />
      </Button>
    </div>
  );
}

/** The condition card badges only condition-side errors; action badges ride `invalidActions`. */
const isConditionInvalid = (ruleErrors: RuleErrors | undefined): boolean =>
  !!ruleErrors && (ruleErrors.empty || ruleErrors.conditionLeaves.length > 0);

const sameInvalidActions = (a: ActionErrorAddress[], b: ActionErrorAddress[]): boolean =>
  a.length === b.length &&
  a.every((address, i) => address.branch === b[i].branch && address.index === b[i].index);

/**
 * `RuleTree`'s memo compare: the validator rebuilds `invalidActions` on every
 * edit, so it's compared structurally; everything else by identity.
 */
const ruleTreePropsEqual = (prev: RuleTreeProps, next: RuleTreeProps): boolean =>
  (Object.keys(next) as (keyof RuleTreeProps)[]).every((key) =>
    key === 'invalidActions'
      ? sameInvalidActions(prev.invalidActions, next.invalidActions)
      : Object.is(prev[key], next[key]),
  );

function CanvasTree({ editor }: { editor: WorkflowEditorState }) {
  const { rules, selection, select, addAction, errors } = editor;
  return (
    <div className="flex flex-col items-center">
      <TriggerPill label={workflowTriggerTypeTitle[editor.context.triggerType]} />
      <BranchRow
        items={rules.map((rule, ruleIndex) => ({
          key: `rule-${ruleIndex}`,
          content: (
            <RuleTree
              rule={rule}
              ruleIndex={ruleIndex}
              selection={selection?.ruleIndex === ruleIndex ? selection : null}
              invalid={isConditionInvalid(errors.rules[ruleIndex])}
              invalidActions={errors.rules[ruleIndex]?.actions ?? []}
              onSelect={select}
              addAction={addAction}
            />
          ),
        }))}
      />
    </div>
  );
}

interface RuleTreeProps {
  rule: EditorRule;
  ruleIndex: number;
  /** The selection when it targets this rule, else `null` — kept stable for memo. */
  selection: WorkflowEditorSelection;
  invalid: boolean;
  /** This rule's badged action addresses. */
  invalidActions: ActionErrorAddress[];
  onSelect: WorkflowEditorState['select'];
  addAction: WorkflowEditorState['addAction'];
}

/**
 * One rule's subtree, memoized on its own slice (stable callbacks, narrowed
 * selection) so a leaf edit re-renders only the edited rule's node.
 */
const RuleTree = memo(
  ({ rule, ruleIndex, selection, invalid, invalidActions, onSelect, addAction }: RuleTreeProps) => {
    const selected = selection?.kind === 'condition' && selection.ruleIndex === ruleIndex;
    const selectCondition = () => onSelect({ kind: 'condition', ruleIndex });
    const conditionIcon = <SplitIcon className="size-3.5" />;

    return (
      <div className="flex flex-col items-center">
        {rule.condition.children.length === 0 ? (
          <AddPill
            icon={conditionIcon}
            label="Add conditions"
            selected={selected}
            invalid={invalid}
            onClick={selectCondition}
          />
        ) : (
          <NodeCard
            className="w-72"
            icon={conditionIcon}
            title="Conditions"
            summary={rule.name || <span className="text-muted-foreground">Untitled rule</span>}
            selected={selected}
            invalid={invalid}
            onSelect={selectCondition}
          />
        )}
        <BranchRow
          items={[
            {
              key: 'actions',
              label: 'If true',
              content: (
                <ActionBranch
                  ruleIndex={ruleIndex}
                  branch="actions"
                  actions={rule.actions}
                  selection={selection}
                  invalidActions={invalidActions}
                  onSelect={onSelect}
                  addAction={addAction}
                />
              ),
            },
            {
              key: 'elseActions',
              label: 'If false',
              content: (
                <ActionBranch
                  ruleIndex={ruleIndex}
                  branch="elseActions"
                  actions={rule.elseActions}
                  selection={selection}
                  invalidActions={invalidActions}
                  onSelect={onSelect}
                  addAction={addAction}
                />
              ),
            },
          ]}
        />
      </div>
    );
  },
  ruleTreePropsEqual,
);
RuleTree.displayName = 'RuleTree';

interface ActionBranchProps {
  ruleIndex: number;
  branch: EditorActionBranch;
  actions: EditorRule['actions'];
  selection: WorkflowEditorSelection;
  /** The rule's badged action addresses (both branches). */
  invalidActions: ActionErrorAddress[];
  onSelect: WorkflowEditorState['select'];
  addAction: WorkflowEditorState['addAction'];
}

function ActionBranch({
  ruleIndex,
  branch,
  actions,
  selection,
  invalidActions,
  onSelect,
  addAction,
}: ActionBranchProps) {
  if (actions.length === 0) {
    return (
      <AddPill
        icon={<ArrowRightIcon className="size-3.5" />}
        label="Add action"
        onClick={() => addAction(ruleIndex, branch)}
      />
    );
  }
  return (
    <div className="flex flex-col items-center">
      {actions.map((action, actionIndex) => {
        const selected =
          selection?.kind === 'action' &&
          selection.ruleIndex === ruleIndex &&
          selection.branch === branch &&
          selection.actionIndex === actionIndex;
        return (
          // actions have no minted ids; index keys suffice
          <Fragment key={actionIndex}>
            {actionIndex > 0 && <ConnectorDown />}
            <NodeCard
              icon={<ArrowRightIcon className="size-3.5" />}
              title="Action"
              summary={getActionSummary(action)}
              selected={selected}
              invalid={invalidActions.some(
                (address) => address.branch === branch && address.index === actionIndex,
              )}
              onSelect={() => onSelect({ kind: 'action', ruleIndex, branch, actionIndex })}
            />
          </Fragment>
        );
      })}
      <ConnectorDown />
      <AddNodeButton aria-label="Add action" onClick={() => addAction(ruleIndex, branch)} />
    </div>
  );
}
